// AppRoutes.tsx
/**
 * The app's route table. Extracted from App.tsx (F3).
 *
 * Everything except the catch-all is lazily loaded, so each route carries its
 * own Suspense fallback.
 */

import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route } from 'react-router-dom';
import { LoadingScreen } from './components/shared/LoadingScreen';
import RequireTerms from './components/Auth/RequireTerms';

// Lazy load admin dashboard for code splitting
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
// Lazy load profile settings
const ProfileSettings = lazy(() => import('./components/Settings/ProfileSettings'));
// UI Phase 1: dev-only design-kit gallery (tokens + primitives) at /design.
const DesignKitPreview = lazy(() => import('./pages/DesignKitPreview'));
// Public legal pages (unauthenticated-reachable; FERPA/consent workstream).
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
// Shareable invite link: /join/<token> — stashes the code, then auth → gate.
const JoinPage = lazy(() => import('./components/Auth/JoinPage'));

const lazyRoute = (node: ReactNode, message = 'Loading...'): ReactNode => (
  <Suspense fallback={<LoadingScreen message={message} />}>{node}</Suspense>
);

export interface AppRoutesProps {
  /** The authenticated app, rendered on the catch-all route. */
  children: ReactNode;
}

const AppRoutes = ({ children }: AppRoutesProps) => (
  <Routes>
    {/* Authenticated pages sit behind the terms gate (plan §3.3); the
        catch-all's AppContent applies it too. */}
    <Route
      path="/admin/*"
      element={<RequireTerms>{lazyRoute(<AdminLayout />, 'Loading admin dashboard...')}</RequireTerms>}
    />
    <Route
      path="/settings"
      element={<RequireTerms>{lazyRoute(<ProfileSettings />, 'Loading settings...')}</RequireTerms>}
    />
    <Route path="/join/:token" element={lazyRoute(<JoinPage />)} />
    <Route path="/privacy" element={lazyRoute(<PrivacyPolicyPage />)} />
    <Route path="/terms" element={lazyRoute(<TermsPage />)} />
    {process.env.NODE_ENV === 'development' && (
      <Route path="/design" element={lazyRoute(<DesignKitPreview />)} />
    )}
    <Route path="/*" element={children} />
  </Routes>
);

export default AppRoutes;
